import { motion } from "framer-motion";
import type { ReactNode } from "react";

/**
 * Beyond Sure — Tabs primitive (P0).
 *
 * The canonical tab pair used on My Policies (Policies / Family), Add
 * Policy (Manual / Upload), OCR Pro workspace (Fields / Tables / Full text),
 * and any future filter group.
 *
 * Visual rules:
 *   - Tabs sit on a hairline border-bottom
 *   - Active tab gets brand-deep underline via shared `layoutId` (smooth
 *     spring slide between tabs)
 *   - Optional count badge to the right of each label (e.g. "Policies 4")
 *   - role="tablist" + role="tab" + aria-selected for screen readers
 *
 * Controlled — pass `value` + `onChange`. Each tab carries an `id` (string)
 * for the controlled state, the user-visible `label`, and an optional
 * `count` and `disabled` flag.
 */

export interface TabItem<T extends string = string> {
  id: T;
  label: string;
  /** Optional count badge — shown as a small pill after the label. */
  count?: number;
  disabled?: boolean;
  /** Optional testid forwarded to the button. */
  testid?: string;
}

interface TabsProps<T extends string> {
  items: ReadonlyArray<TabItem<T>>;
  value: T;
  onChange: (id: T) => void;
  /** Unique key for the layout animation (lets multiple tab groups coexist). */
  layoutId?: string;
  /** Tabs alignment: "start" (default) or "stretch" (equal-width). */
  align?: "start" | "stretch";
  /** Optional content rendered to the right of the tab list — search, filter, action. */
  trailing?: ReactNode;
  ariaLabel?: string;
  className?: string;
}

export function Tabs<T extends string>({
  items,
  value,
  onChange,
  layoutId = "tabs-underline",
  align = "start",
  trailing,
  ariaLabel,
  className,
}: TabsProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={[
        "flex items-center gap-1 mb-4 border-b border-[var(--color-ink-08)]",
        align === "stretch" ? "[&>button]:flex-1 [&>button]:justify-center" : "",
        className ?? "",
      ].join(" ")}
    >
      {items.map((item) => {
        const active = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            aria-disabled={item.disabled}
            disabled={item.disabled}
            onClick={() => !item.disabled && onChange(item.id)}
            data-testid={item.testid}
            className={[
              "relative inline-flex items-center gap-2 px-3 sm:px-4 py-2",
              "text-[13.5px] font-medium",
              "transition-colors duration-[var(--duration-fast)]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] focus-visible:ring-offset-2 rounded-t-md",
              active
                ? "text-[var(--color-ink)]"
                : "text-[var(--color-ink-60)] hover:text-[var(--color-ink)]",
              item.disabled ? "opacity-40 cursor-not-allowed" : "",
            ].join(" ")}
          >
            <span>{item.label}</span>
            {typeof item.count === "number" ? (
              <span
                className={[
                  "inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full",
                  "text-[10px] font-semibold",
                  active
                    ? "bg-[var(--color-brand-deep)] text-[var(--color-paper)]"
                    : "bg-[var(--color-ink-08)] text-[var(--color-ink-60)]",
                ].join(" ")}
              >
                {item.count}
              </span>
            ) : null}
            {active ? (
              <motion.span
                layoutId={layoutId}
                className="absolute inset-x-0 -bottom-px h-[2px] bg-[var(--color-brand-deep)] rounded-full"
                transition={{ type: "spring", stiffness: 380, damping: 32 }}
              />
            ) : null}
          </button>
        );
      })}
      {trailing ? <div className="ml-auto pl-2">{trailing}</div> : null}
    </div>
  );
}

export default Tabs;
